<script setup lang="ts">
import type { CrashCandle } from '~/types/crash-pre-game';

type Props = {
  /** Velas na ordem do core: índice 0 = a mais recente. */
  candles: CrashCandle[];
};

const { candles } = defineProps<Props>();

// TODAS as velas que o core mandou (ele já corta em 48), sem amostra própria: o
// traçado e a grade logo abaixo mostram a MESMA janela. Com um corte só aqui, o
// "Pico" seria calculado sobre um pedaço e o jogador acharia na grade uma
// rodada maior que a anunciada como o pico.
//
// O traçado lê da ESQUERDA (mais antiga) pra DIREITA (mais recente), que é como
// um gráfico de tempo se lê — o oposto da grade, onde a mais recente vem
// primeiro. Por isso o reverse: a ordem do core não muda, só esta leitura.
const points = computed(() => candles.slice().reverse());

// Topo da escala em LOG. Multiplicador é grandeza multiplicativa: numa escala
// linear uma vela de 50x achata todas as de 1.xx no chão e o gráfico vira uma
// linha reta com um espeto. Em log, a distância de 1x→2x é a mesma de 5x→10x,
// que é como o jogador lê o jogo.
const LOG_FLOOR = 1; // toda vela estoura em 1.00x ou mais

const scaleTop = computed(() => {
  const max = points.value.reduce((acc, c) => Math.max(acc, c.multiplier), 0);

  // Piso de 3x pra que uma janela só de velas azuis não vire um serrote
  // gigante, e 15% de folga pro pico não encostar na borda de cima.
  return Math.max(max * 1.15, 3);
});

// 0 (topo) → 100 (base), no espaço do viewBox.
function toY(multiplier: number): number {
  const span = Math.log(scaleTop.value) - Math.log(LOG_FLOOR);

  if (span <= 0) return 100;

  const ratio =
    (Math.log(Math.max(multiplier, LOG_FLOOR)) - Math.log(LOG_FLOOR)) / span;

  return 100 - Math.min(Math.max(ratio, 0), 1) * 100;
}

function toX(index: number): number {
  const last = points.value.length - 1;

  return last <= 0 ? 100 : (index / last) * 100;
}

const coords = computed(() =>
  points.value.map((candle, index) => ({
    candle,
    x: toX(index),
    y: toY(candle.multiplier),
  })),
);

// Dois pontos é o mínimo pra existir uma linha; com menos, o bloco não entra.
const hasLine = computed(() => coords.value.length >= 2);

const linePath = computed(() =>
  coords.value
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(' '),
);

// Mesma linha fechada contra a base, pro preenchimento em gradiente.
const areaPath = computed(() => `${linePath.value} L100,100 L0,100 Z`);

// Guias nas faixas que o core usa pra escolher a cor (2x e 10x). Só entram se
// couberem na escala atual — uma guia de 10x numa janela que não passou de 2x
// seria uma linha colada no topo dizendo nada.
const guides = computed(() =>
  [
    { value: 2, label: '2x' },
    { value: 10, label: '10x' },
  ]
    .filter((g) => g.value < scaleTop.value)
    .map((g) => ({ ...g, y: toY(g.value) })),
);

// Destaques em cima da linha: as velas rosas (≥ 10x, o que o jogador procura) e
// sempre a última. Marcar os 30 pontos viraria poluição.
const highlights = computed(() => {
  const lastIndex = coords.value.length - 1;

  return coords.value.filter(
    (p, i) => p.candle.color === 'pink' && i !== lastIndex,
  );
});

const lastPoint = computed(() => coords.value[coords.value.length - 1] ?? null);

const peak = computed(() => {
  if (!points.value.length) return null;

  return points.value.reduce((acc, c) =>
    c.multiplier > acc.multiplier ? c : acc,
  );
});

function formatMultiplier(multiplier: number): string {
  return `${multiplier.toFixed(2)}x`;
}

// Gradiente com ID único: dois blocos do mesmo jogo na mesma página (sheet
// mobile + cartão de pré-jogo) compartilhariam o `url(#...)` e o segundo
// herdaria o gradiente do primeiro.
const uid = useId();
const strokeId = computed(() => `crash-stroke-${uid}`);
const fillId = computed(() => `crash-fill-${uid}`);
</script>

<template>
  <div
    v-if="hasLine"
    class="crash-spark"
  >
    <div class="crash-spark__head">
      <span class="crash-spark__caption">
        Últimas {{ coords.length }} rodadas
      </span>

      <span
        v-if="peak"
        class="crash-spark__peak"
      >
        Pico {{ formatMultiplier(peak.multiplier) }}
      </span>
    </div>

    <div class="crash-spark__plot">
      <!-- Calha do eixo Y: os rótulos das guias moram FORA da área de
           plotagem. Dentro dela eles disputavam o canto esquerdo com o traçado
           e com as bolinhas das velas rosas — e bolinha desenha depois do
           rótulo, então nem fundo opaco resolvia. -->
      <div class="crash-spark__gutter">
        <span
          v-for="guide in guides"
          :key="`label-${guide.value}`"
          class="crash-spark__guide-label"
          :style="{ top: `${guide.y}%` }"
        >
          {{ guide.label }}
        </span>
      </div>

      <!-- O SVG e os marcadores em HTML precisam medir a MESMA caixa. Como
           `left`/`top` de um absoluto resolvem contra o PADDING box do pai, e o
           SVG ocupa só o CONTENT box, o padding que abre espaço pro rótulo da
           vela atual deslocava cada bolinha pra direita da linha. Por isso o
           padding fica no .plot e tudo que se posiciona mora neste .canvas, que
           não tem padding nenhum. -->
      <div class="crash-spark__canvas">
        <!-- preserveAspectRatio="none" estica o desenho na largura disponível; os
           traços seguram a espessura com vector-effect, e tudo que não pode
           distorcer (pontos, rótulos) é HTML posicionado por cima, não SVG. -->
        <svg
          aria-hidden="true"
          class="crash-spark__svg"
          preserveAspectRatio="none"
          viewBox="0 0 100 100"
        >
          <defs>
            <linearGradient
              :id="strokeId"
              x1="0"
              x2="1"
              y1="0"
              y2="0"
            >
              <stop
                offset="0%"
                stop-color="#37c6f0"
              />
              <stop
                offset="55%"
                stop-color="#a560f5"
              />
              <stop
                offset="100%"
                stop-color="#ec5a98"
              />
            </linearGradient>

            <linearGradient
              :id="fillId"
              x1="0"
              x2="0"
              y1="0"
              y2="1"
            >
              <stop
                offset="0%"
                stop-color="#37c6f0"
                stop-opacity="0.35"
              />
              <stop
                offset="100%"
                stop-color="#37c6f0"
                stop-opacity="0"
              />
            </linearGradient>
          </defs>

          <line
            v-for="guide in guides"
            :key="guide.value"
            class="crash-spark__guide"
            vector-effect="non-scaling-stroke"
            x1="0"
            x2="100"
            :y1="guide.y"
            :y2="guide.y"
          />

          <path
            class="crash-spark__area"
            :d="areaPath"
            :fill="`url(#${fillId})`"
          />

          <path
            class="crash-spark__line"
            :d="linePath"
            fill="none"
            path-length="1"
            :stroke="`url(#${strokeId})`"
            stroke-linecap="round"
            stroke-linejoin="round"
            vector-effect="non-scaling-stroke"
          />
        </svg>

        <span
          v-for="point in highlights"
          :key="`hl-${point.candle.roundTime}`"
          class="crash-spark__dot"
          :style="{ left: `${point.x}%`, top: `${point.y}%` }"
        />

        <!-- A vela mais recente fica sempre na borda direita e pulsa: é o ponto
           que o jogador procura primeiro quando bate o olho. -->
        <span
          v-if="lastPoint"
          class="crash-spark__now"
          :style="{ top: `${lastPoint.y}%` }"
        >
          <span class="crash-spark__now-ping" />
          <span class="crash-spark__now-core" />
        </span>

        <!-- O valor da vela atual entra POR DENTRO do gráfico, à esquerda do
             ponto. Mantê-lo fora custava uma faixa morta na direita do card e
             encolhia o traçado. -->
        <span
          v-if="lastPoint"
          class="crash-spark__now-value"
          :style="{ top: `${lastPoint.y}%` }"
        >
          {{ formatMultiplier(lastPoint.candle.multiplier) }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.crash-spark {
  margin-bottom: 12px;
  padding: 10px 10px 8px;
  border-radius: 12px;
  background: linear-gradient(
    180deg,
    rgba(55, 198, 240, 0.08),
    rgba(55, 198, 240, 0)
  );
  border: 1px solid #1c2740;
}

.crash-spark__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.crash-spark__caption {
  color: rgba(255, 255, 255, 0.45);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.crash-spark__peak {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(236, 90, 152, 0.14);
  color: #ff8fbe;
  font-size: 10px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.crash-spark__plot {
  display: grid;
  /* Calha do eixo Y + área de plotagem. O recuo é CSS, não unidade de viewBox:
     com preserveAspectRatio="none" um x1 no SVG esticaria junto com a largura. */
  grid-template-columns: 26px minmax(0, 1fr);
  /* A linha PRECISA ser definida. Com linha auto, o `height: 100%` do canvas
     e a altura da linha viram dependência circular (a linha mede o conteúdo, o
     conteúdo mede a linha) e o Chromium resolve pra centenas de px: o SVG
     vazava do card e desenhava por cima da grade de velas. 100% resolve contra
     o `height` abaixo, que segue sendo a única fonte da altura. */
  grid-template-rows: 100%;
  align-items: stretch;
  height: 92px;
}

/* Mesma altura do canvas, então o `top` em % dos rótulos continua batendo com
   a posição das guias lá dentro. */
.crash-spark__gutter {
  position: relative;
}

/* A régua de todo mundo: o SVG preenche esta caixa e os marcadores se
   posicionam contra ela. Sem padding, sem borda — senão os dois voltam a medir
   coisas diferentes. */
.crash-spark__canvas {
  position: relative;
  width: 100%;
  height: 100%;
}

.crash-spark__svg {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.crash-spark__guide {
  stroke: rgba(255, 255, 255, 0.14);
  stroke-width: 1;
  stroke-dasharray: 3 4;
}

.crash-spark__line {
  stroke-width: 2;
  /* O traçado se desenha UMA vez, na entrada. path-length="1" normaliza o
     comprimento, então o mesmo keyframe serve pra 5 ou pra 50 velas. Nada de
     `transition`: o reset global do main.scss mata transition no app inteiro. */
  stroke-dasharray: 1;
  animation: crash-spark-draw 900ms ease-out both;
}

.crash-spark__area {
  animation: crash-spark-fade 900ms ease-out both;
}

/* Encostado na borda direita da calha: o número fica colado no início da sua
   própria guia. Sem chip de fundo — na calha não há nada por baixo dele. */
.crash-spark__guide-label {
  position: absolute;
  right: 5px;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.55);
  font-size: 9px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}

/* Velas rosas no traçado: o marcador que explica sozinho por que a linha
   disparou naquele ponto. */
.crash-spark__dot {
  position: absolute;
  width: 7px;
  height: 7px;
  margin: -3.5px 0 0 -3.5px;
  border-radius: 50%;
  background: #ec5a98;
  box-shadow: 0 0 0 2px rgba(236, 90, 152, 0.25);
  pointer-events: none;
}

.crash-spark__now {
  position: absolute;
  /* A última vela É o ponto x=100% do traçado. Ancorar pela direita colocaria
     o ponto a uma largura-de-texto de distância da linha. */
  left: 100%;
  pointer-events: none;
}

.crash-spark__now-ping,
.crash-spark__now-core {
  position: absolute;
  left: 0;
  border-radius: 50%;
}

.crash-spark__now-core {
  width: 8px;
  height: 8px;
  margin: -4px 0 0 -4px;
  background: #ffffff;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
}

.crash-spark__now-ping {
  width: 18px;
  height: 18px;
  margin: -9px 0 0 -9px;
  background: rgba(255, 255, 255, 0.55);
  animation: crash-spark-ping 1.8s ease-out infinite;
}

.crash-spark__now-value {
  position: absolute;
  right: 12px;
  padding: 2px 6px;
  border-radius: 6px;
  /* Chip com fundo: o rótulo passa por cima do traçado e da área preenchida, e
     sem ele o número some no meio do gradiente. */
  background: rgba(7, 11, 20, 0.72);
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  transform: translateY(-50%);
  pointer-events: none;
}

@keyframes crash-spark-draw {
  from {
    stroke-dashoffset: 1;
  }

  to {
    stroke-dashoffset: 0;
  }
}

@keyframes crash-spark-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes crash-spark-ping {
  0% {
    opacity: 0.55;
    transform: scale(0.4);
  }

  70%,
  100% {
    opacity: 0;
    transform: scale(1.15);
  }
}

/* Quem pediu menos movimento recebe o gráfico pronto, sem desenho nem pulso. */
@media (prefers-reduced-motion: reduce) {
  .crash-spark__line,
  .crash-spark__area,
  .crash-spark__now-ping {
    animation: none;
  }

  .crash-spark__line {
    stroke-dashoffset: 0;
  }

  .crash-spark__now-ping {
    opacity: 0;
  }
}
</style>
